import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_16, SETTING_API_17 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

// Types and Interfaces
interface ReasonCategoryItem {
  reasoncatcode?: string | number;
  reasoncatname?: string;
  doccatname?: string;
}

interface ReasonTypeItem {
  id?: string | number;
  reasontypecode?: string | number;
  reasoncatcode?: string | number;
  descrip?: string;
  doctype?: string;
  docsavecode?: string | number;
}

interface ReasonDetails {
  descrip: string;
  reasoncatcode: string | number;
  reasontypecode: string | number;
  doctype?: string;
  docsavecode?: string | number;
}

interface ErrorState {
  descrip?: string;
  doccatname?: string;
  reasoncatcode?: string;
  [key: string]: string | undefined;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  RESTCODE?: string;
  USERNAME?: string;
  COMPCODE?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: ReasonTypeItem;
  actionText?: string;
  selectedreasoncatcode?: string | number;
  selectedreasoncatname?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreateReason(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const [data, setData] = useState<ReasonTypeItem[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const location = useLocation();
  const { selectedItem } = (location.state as LocationState) || {};
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [alertMessage, setAlertMessage] = useState<string>("");
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, RESTCODE, USERNAME, COMPCODE } =
    SYS_USER.USER_PARAM || {};
  const isEdit = (location.state as LocationState)?.actionText === "Edit";

  const [datadoccat, setDatadoccat] = useState<ReasonCategoryItem[] | null>(null);
  const [selectedItemDetails, setSelectedItemDetails] = useState<ReasonDetails>({
    descrip: "",
    reasoncatcode: "",
    reasontypecode: "",
  });

  const [items, setItems] = useState<ReasonTypeItem[]>([]);

  const { selectedreasoncatcode, selectedreasoncatname } =
    (location.state as LocationState) || {};

  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        doctype: selectedItem.doctype || "",
        descrip: selectedItem.descrip || "",
        reasontypecode: selectedItem.reasontypecode || "",
        reasoncatcode: selectedItem.reasoncatcode || "",
        docsavecode: selectedItem.docsavecode || "",
      });
    }
  }, [selectedItem]);

  console.log("Selected Item Details:", selectedItemDetails);

  useEffect(() => {
    setPageData({
      title: "Reason Types",
    } as PageData);
  }, [setPageData]);

  const goBackToView = (): void => {
    navigate("/vreasontypes");
  };

  const cleanInputValue = (name: string, value: string): string => {
    if (typeof value !== "string") return value;
    // Example: clean leading spaces for all inputs, or per field if needed
    return value.replace(/^\s+/, ""); // remove leading spaces
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    // Auto-clean the input (remove leading spaces)
    const cleanedValue = cleanInputValue(name, value);
    const trimmedValue =
      typeof cleanedValue === "string" ? cleanedValue.trim() : cleanedValue;

    // Prevent initial space only (optional after clean)
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate max length
    if (typeof cleanedValue === "string" && cleanedValue.length > 30) {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: "Input cannot exceed 30 characters.",
      }));
      return;
    }

    // Validate empty or space-only input
    if (typeof cleanedValue === "string" && trimmedValue === "") {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: "Input cannot be empty or just spaces.",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }

    // Always update with the cleaned value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: cleanedValue,
    }));
  };

  const handleInputChangecode = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Prevent initial space only
    if (typeof value === "string" && value.length === 1 && value[0] === " ") {
      return;
    }

    // Validate max length
    if (typeof value === "string" && value.length > 6) {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input cannot exceed 5 characters.",
      }));
      return;
    }

    // Validate empty string
    if (typeof value === "string" && trimmedValue === "") {
      setError((prevError) => ({
        ...prevError,
        [name]: "Input cannot be empty or just spaces.",
      }));
    } else {
      setError((prevError) => ({
        ...prevError,
        [name]: "",
      }));
    }

    // Always update the value
    setSelectedItemDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_16, "get", {
          STS: CON_STATUS_ACTIVE,
        });

        setDatadoccat(result as ReasonCategoryItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_17, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          reasoncatcode: selectedreasoncatcode,
          STS: CON_STATUS_ACTIVE || "D",
        });

        setData(result as ReasonTypeItem[]);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        setError(err as ErrorState);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE, selectedreasoncatcode]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const result = await externalgetApi(
          `${SETTING_API_17}/${selectedreasoncatcode}`,
          "get",
          { reasoncatcode: selectedreasoncatcode }
        );

        setItems(result as ReasonTypeItem[]);
        console.log("result", result);
      } catch (err) {
        setError(err as ErrorState);
        console.error("Fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedreasoncatcode) {
      fetchData();
    }
  }, [selectedreasoncatcode]);

  const handleCancel = (): void => {
    setSelectedItemDetails({
      doctype: "",
      descrip: "",
      docsavecode: "",
      reasoncatcode: "",
      reasontypecode: "",
    });
  };

  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);
    const { descrip } = selectedItemDetails;

    const requiredFields = [
      {
        name: "doccatname",
        value: selectedreasoncatcode,
        label: "Reason Category Name",
      },
      { name: "descrip", value: descrip, label: "Reason" },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) => !field.value
    );
    
    if (missingRequiredFields.length > 0) {
      const newError: ErrorState = {};
      missingRequiredFields.forEach((field) => {
        newError[field.name] = `${field.label} is required.`;
      });
      setError(newError);

      setAlertType("error");
      setAlertMessage("Please Fill All the Required Fields.");
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    if (data) {
      const isDuplicate = data.some((item) => {
        const isSameDescription =
          item.descrip?.trim().toLowerCase() === descrip?.trim().toLowerCase();

        // Always use the selected category from location/state
        const isSameCategory =
          String(item.reasoncatcode) === String(selectedreasoncatcode);

        // Exclude the current record when editing
        const isDifferentRecord =
          !isEdit || item.reasontypecode !== selectedItemDetails.reasontypecode;

        return isDifferentRecord && isSameDescription && isSameCategory;
      });

      if (isDuplicate) {
        setAlertMessage("Reason already exists in this category.");
        setAlertType("warning");
        setAlertOpen(true);
        setIsButtonDisabled(false);
        return;
      }
    }

    const BRAND_CODE = selectedItem ? selectedItem.reasontypecode : 0;
    console.log("Brand Code:", BRAND_CODE);

    const staticValues = {
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      reasoncatcode: selectedreasoncatcode
        ? parseInt(String(selectedreasoncatcode), 10)
        : selectedItemDetails.reasoncatcode
        ? parseInt(String(selectedItemDetails.reasoncatcode), 10)
        : 0,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    const payload = {
      ...selectedItemDetails,
      ...staticValues,
      reasontypecode: BRAND_CODE,
    };

    try {
      console.log("Payload for API call:", payload);
      const responseData = await externalpostApi(SETTING_API_17, payload);
      console.log("Success:", responseData);
      setAlertMessage(
        `${isEdit ? "Updated Successfully!" : "Created Successfully!"}`
      );
      setAlertType("success");
      setAlertOpen(true);
      
      setSelectedItemDetails({
        doctype: "",
        descrip: "",
        docsavecode: "",
        reasoncatcode: "",
        reasontypecode: "",
      });
    } catch (error) {
      const err = error as ApiError;
      console.error("Error:", err);
      setAlertType("error");
      setAlertMessage(err.response?.data?.message || "An unexpected error occurred.");
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const isDescripInList = items.some(
    (item) =>
      item.descrip?.trim().toLowerCase() ===
      selectedItemDetails.descrip?.trim().toLowerCase()
  );

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="icon_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            Home
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Reason Types
          </Link>
        </Breadcrumbs>
      </Box>
      
      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 8, sm: 10, md: 10, lg: 10, xl: 10 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {isEdit ? "Edit Reason " : "Create Reason "} :{" "}
                {selectedreasoncatname}
              </Typography>
            </Grid>
          </Grid>
        </AppBar>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={styles.add_form_page_container}
        >
          <Grid
            container
            spacing={2}
            sx={styles.add_form_page_grid_container}
            alignItems="stretch"
          >
            {/* Left Section */}
            <Grid
              size={{ xs: 12, sm: 6, md: 5, lg: 5, xl: 5 }}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Typography sx={styles.appbar_title}>Reasons</Typography>
              <List
                dense
                sx={{ p: 0, mt: 2, maxHeight: 400, overflow: "auto" }}
              >
                {items.map((item, index) => (
                  <ListItem
                    key={item.id || index}
                    sx={{
                      borderRadius: 2,
                      boxShadow: "0px 1px 5px rgba(0,0,0,0.1)",
                      mb: 1,
                      bgcolor: "#fff",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <ListItemText
                      id={`txt_reason_descrip_${index}`}
                      primary={item.descrip}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#333",
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        id="btn_add_reason"
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{
                          bgcolor: "#1976d2",
                          "&:hover": { bgcolor: "#115293" },
                          textTransform: "none",
                        }}
                        onClick={() =>
                          setSelectedItemDetails((prevDetails) => ({
                            ...prevDetails,
                            descrip: item.descrip || "", // Set the text field
                          }))
                        }
                      >
                        Add
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Vertical Divider */}
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            {/* Right Section */}
            <Grid
              size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TextField
                id="txt_reasoncatname"
                label="Reason Category"
                variant="outlined"
                fullWidth
                required
                size="small"
                name="reasoncatname"
                value={selectedreasoncatname || ""}
                disabled
                sx={{ mt: 2 }}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />

              <TextField
                id="txt_descrip"
                label="Reason"
                variant="outlined"
                fullWidth
                required
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChange}
                error={!!error.descrip}
                sx={{ mt: 2 }}
                helperText={error.descrip}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
                disabled={isDescripInList}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_clear"
            variant="outlined"
            size="medium"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            Clear
          </Button>
          <Button
            id="btn_confirm"
            variant="contained"
            size="medium"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            onClick={handleConfirmClick}
            disabled={isButtonDisabled}
          >
            {isEdit ? "Modify" : "Confirm"}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateReason;